import { ThemeOptions } from '@mui/material';
import type { Theme } from '@mui/material/styles';

// Use a plain string for the font family to allow compatibility with both Next.js and Node.js.
// --font-poppins is set by next/font in the root layout and carries its size-matched
// fallback, which avoids layout shift while Poppins loads; outside Next it falls back to Poppins.
const defaultFontFamily = 'var(--font-poppins, Poppins), sans-serif';

const themeColors = {
  heading: '#303540',
  title: '#414959',
  body: '#303540',
  bodyNotActive: '#5B667B',
  label: '#303540',
  labelNotActive: '#8F9AAD',
  controlLabel: '#8F9AAD',
  controlText: '#303540',
  defaultBorder: '#D9DCE3',
  disabledBorder: '#D9DCE3',
  disabledBackground: '#F5F5F5',
  disabledColor: '#D9DCE3',
  focusBorder: 'transparent',
  activeBackground: '#E5F4E7',
  good: '#00A86B',
  activeButton: '#387CA9',
  warningiInfoBackground: '#fef9e7',
  greenStatus: '#3a8245',
  lightGreenStatus: '#90bb39',
  yellowStatus: '#f2dc56',
  redStatus: '#df684b',
  lightRedStatus: '#f58b72',
  defaultBackground: '#FAFAFA',
  inputBackground: '#FFFFFF',
  hoverBackground: '#58719340',
  primaryColor: {
    main: '#48A1DB',
    light: '#E8EDF9',
    dark: '#76BBE8',
    contrastText: '#ffffff',
  },
  secondaryColor: {
    main: '#333333',
    light: '#05080b40',
    dark: '#76BBE8',
    contrastText: '#ffffff',
  },
  invertedText: '#ffffff',
  errorColor: {
    main: '#DB4047',
    light: '#DB404780',
    dark: '#c62828',
    contrastText: '#ffffff',
  },
  warningColor: {
    main: '#CEA71B',
    light: '#CEA71B80',
    dark: '#E4B81C',
    contrastText: '#ffffff',
  },
  infoColor: {
    main: '#8F9AAD',
    light: '#D9DCE3',
    dark: '#76BBE8',
    contrastText: '#ffffff',
  },
  successColor: {
    main: '#26B950',
    light: '#9FE0A9',
    dark: '#26B950',
    contrastText: '#ffffff',
  },
};

const fontFamilyStyle = { fontFamily: defaultFontFamily };

/**
 * Sentry User Feedback uses `--z-index: 100000` on `#sentry-feedback` (@sentry-internal/feedback).
 * Our nav injects CSS to hide the floating actor from `min-width: 960px` up; below that, MUI's
 * default modal tiers (drawer 1200, modal 1300, …) sit under Sentry. We lift those tiers only in
 * that viewport via `theme.breakpoints.down(960)` so desktop/tablet-wide layouts keep stock z-index.
 */
const Z_INDEX_OFFSET_ABOVE_SENTRY_USER_FEEDBACK = 99_001;

/** MUI default z-index values (before theme merge); used so mobile lift matches production ordering */
const MUI_DEFAULT_Z_INDEX = {
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
} as const;

const mobileZIndexAboveSentryFeedback = (
  theme: Theme,
  base: number,
): Record<string, { zIndex: number }> => ({
  [theme.breakpoints.down(960)]: {
    zIndex: base + Z_INDEX_OFFSET_ABOVE_SENTRY_USER_FEEDBACK,
  },
});

// Unified theme object with higher priority properties taking precedence
export const defaultTheme: ThemeOptions = {
  fontName: 'poppins',
  logo: '/assets/images/clients/default/logo.png',
  logoFull: '/assets/images/clients/default/logo-full.png',
  palette: {
    primary: themeColors.primaryColor,
    secondary: themeColors.secondaryColor,
    error: themeColors.errorColor,
    warning: themeColors.warningColor,
    info: themeColors.infoColor,
    success: themeColors.successColor,
    action: {
      disabledBackground: themeColors.disabledBackground,
      disabled: themeColors.disabledColor,
      focus: themeColors.focusBorder,
      active: themeColors.primaryColor.main,
      selected: themeColors.secondaryColor.light,
      hover: themeColors.hoverBackground,
    },
    text: {
      primary: themeColors.body,
      secondary: themeColors.bodyNotActive,
      disabled: themeColors.labelNotActive,
    },
    background: {
      paper: themeColors.defaultBackground,
      default: themeColors.defaultBackground,
    },
    divider: themeColors.disabledBorder,
  },
  typography: {
    ...fontFamilyStyle,
    fontSize: 14,
    allVariants: fontFamilyStyle,
    button: fontFamilyStyle,
    subtitle2: {
      fontSize: '0.8rem',
    },
  },
  components: {
    MuiModal: {
      styleOverrides: {
        root: ({ theme }) =>
          mobileZIndexAboveSentryFeedback(theme, MUI_DEFAULT_Z_INDEX.modal),
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: ({ theme }) =>
          mobileZIndexAboveSentryFeedback(theme, MUI_DEFAULT_Z_INDEX.modal),
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: ({ theme }) =>
          mobileZIndexAboveSentryFeedback(theme, MUI_DEFAULT_Z_INDEX.modal),
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: ({ theme }) =>
          mobileZIndexAboveSentryFeedback(theme, MUI_DEFAULT_Z_INDEX.modal),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: ({ theme }) =>
          mobileZIndexAboveSentryFeedback(theme, MUI_DEFAULT_Z_INDEX.drawer),
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: ({ theme }) =>
          mobileZIndexAboveSentryFeedback(theme, MUI_DEFAULT_Z_INDEX.snackbar),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        popper: ({ theme }) =>
          mobileZIndexAboveSentryFeedback(theme, MUI_DEFAULT_Z_INDEX.tooltip),
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: ({ theme }) =>
          mobileZIndexAboveSentryFeedback(theme, MUI_DEFAULT_Z_INDEX.tooltip),
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: defaultFontFamily + ' !important',
          '&.MuiPaginationItem-root.Mui-selected': {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          },
        }),
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        root: fontFamilyStyle,
      },
    },
    MuiTable: {
      styleOverrides: {
        root: fontFamilyStyle,
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: themeColors.defaultBorder,
          padding: '0.75rem !important',
          '.MuiTypography-root': {
            fontSize: '0.85rem',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          color: themeColors.title,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: fontFamilyStyle,
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: fontFamilyStyle,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          ...fontFamilyStyle,
          border: '1px solid transparent',
        },
      },
    },
    MuiTypography: {
      defaultProps: fontFamilyStyle,
    },
    MuiIcon: {
      defaultProps: {
        color: 'info',
      },
    },
    MuiSvgIcon: {
      defaultProps: {
        color: 'inherit',
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          color: themeColors.bodyNotActive,
          borderRadius: '4px',
          borderColor: themeColors.disabledBorder,
          '&:before': {
            backgroundColor: 'transparent',
          },
          '.MuiButtonBase-root': {
            borderBottomColor: themeColors.disabledBorder,
            borderRadius: 0,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderColor: themeColors.defaultBorder,
          backgroundColor: themeColors.inputBackground,
          fontFamily: defaultFontFamily,
          padding: '0',
          color: themeColors.bodyNotActive,
          '&:before': {
            borderBottomWidth: '0',
          },
          '&.Mui-disabled': {
            borderColor: themeColors.disabledBorder,
            backgroundColor: themeColors.disabledBackground,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          padding: 0,
          backgroundColor: themeColors.inputBackground,
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          borderWidth: '1px',
          borderStyle: 'solid',
          '&:before': {
            borderBottomWidth: '0 !important',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '.MuiInputBase-input': {
            padding: '12px 16px',
          },
          '.MuiAutocomplete-input': {
            padding: '12px 16px !important',
          },
          '.MuiInputLabel-standard': {
            padding: '12px 16px',
          },
          '.MuiInputLabel-shrink': {
            padding: 'inherit',
          },
          '.MuiOutlinedInput-root': {
            paddingInlineEnd: '0',
          },
          '.MuiInputAdornment-root , .MuiAutocomplete-endAdornment': {
            paddingInlineEnd: '8px',
          },
          '.MuiInputBase-root': {
            '.MuiIconButton-root': {
              marginInlineEnd: '8px',
              paddingInlineEnd: '0',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderColor: themeColors.defaultBorder,
        },
        notchedOutline: {
          borderColor: themeColors.defaultBorder,
        },
        multiline: {
          width: '100%',
          borderColor: themeColors.defaultBorder,
        },
      },
    },
  },
};
